import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Lock, Download, Share2, Loader2, MessageSquare, ChevronRight } from 'lucide-react';
import { useAuth } from '../../App';

const SEGMENT_PROMPTS = {
  startup: "Should we pivot our B2B SaaS to target enterprise clients, or double down on SMB self-serve?",
  enterprise: "Should we migrate our customer data infrastructure to a multi-cloud architecture?",
  consultant: "Our client is choosing between Salesforce and HubSpot — what should we recommend?",
  engineering: "Monolith vs microservices for our new payment processing system?",
  academic: "Is my methodology sound? Evaluate this mixed-methods research design for studying remote work productivity.",
  finance: "Should we invest in this Series A AI startup at a $40M valuation given current market conditions?",
  personal: "I have two job offers — one at a startup with equity, one at BigCo with 40% more salary. Help me decide.",
  marketing: "Should we allocate 60% of Q3 budget to paid social or shift toward content marketing and SEO?",
};

const GOVERNANCE_PROMPT = "Our security team flagged a compliance gap in our data processing pipeline. Should we halt the product launch to remediate, or ship with a documented risk acceptance and 90-day remediation plan?";

const VOICE_COLORS = ['#FF4D00', '#00D4FF', '#F2A900', '#A61E17', '#F5F1E8'];
const VOICE_NAMES = ['The Strategist', 'Devil\'s Advocate', 'The Pragmatist', 'Risk Analyst', 'The Synthesizer'];

export default function DebateSandbox({
  preloadedPrompt = null,
  segment = null,
  governance = false,
  resourceLink = null,
  className = '',
}) {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState(
    preloadedPrompt || (governance ? GOVERNANCE_PROMPT : (segment ? SEGMENT_PROMPTS[segment] : ''))
  );
  const [isDebating, setIsDebating] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showAuthGate, setShowAuthGate] = useState(false);

  const handleStartDebate = useCallback(() => {
    if (!prompt.trim()) return;
    setIsDebating(true);
    setMessages([]);

    // Simulate streaming debate voices
    const voices = VOICE_NAMES.map((name, i) => ({ name, color: VOICE_COLORS[i] }));
    let msgIndex = 0;

    const simulateVoice = () => {
      if (msgIndex >= voices.length) {
        setIsDebating(false);
        return;
      }
      const voice = voices[msgIndex];
      setMessages(prev => [...prev, {
        voice: voice.name,
        color: voice.color,
        text: getSimulatedResponse(voice.name, prompt),
        typing: true,
      }]);

      setTimeout(() => {
        setMessages(prev => prev.map((m, i) =>
          i === prev.length - 1 ? { ...m, typing: false } : m
        ));
        msgIndex++;
        setTimeout(simulateVoice, 800);
      }, 1500 + Math.random() * 1000);
    };

    setTimeout(simulateVoice, 600);
  }, [prompt]);

  const handleValueLockAction = (action) => {
    if (!user) {
      setShowAuthGate(true);
      return;
    }
    // If authenticated, perform the action
    if (action === 'save') {
      // save logic
    } else if (action === 'share') {
      // share logic
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] overflow-hidden">
        {/* Noise texture */}
        <div className="grain-overlay absolute inset-0 pointer-events-none" />

        {/* Header */}
        <div className="relative flex items-center justify-between px-5 py-3 border-b border-[rgba(245,241,232,0.08)]">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-[#FF4D00] animate-thermal-pulse" />
            <span className="font-mono text-xs text-[rgba(245,241,232,0.72)] tracking-wider uppercase">
              {governance ? 'Governance Sandbox' : 'Live Debate Sandbox'}
            </span>
          </div>
          {!user && (
            <span className="font-mono text-[10px] text-[rgba(245,241,232,0.48)] tracking-wider">
              ECONOMY MODE
            </span>
          )}
        </div>

        {/* Prompt Input */}
        <div className="relative p-5">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe your decision or dilemma..."
            rows={3}
            className="w-full bg-[#0A0A0A] border border-[rgba(245,241,232,0.08)] text-[#F5F1E8] px-4 py-3 text-sm font-body placeholder:text-[rgba(245,241,232,0.32)] resize-none focus:outline-none focus:border-[#FF4D00] focus:shadow-[0_0_12px_rgba(255,77,0,0.15)] transition-all"
          />
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              {resourceLink && (
                <a
                  href={resourceLink.url}
                  className="text-xs text-[#00D4FF] hover:text-[#33DDFF] font-mono transition-colors"
                >
                  {resourceLink.label} →
                </a>
              )}
            </div>
            <button
              onClick={handleStartDebate}
              disabled={isDebating || !prompt.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] active:bg-[#CC3D00] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {isDebating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Debating...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Start Debate
                </>
              )}
            </button>
          </div>
        </div>

        {/* Debate Output */}
        <AnimatePresence>
          {messages.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="border-t border-[rgba(245,241,232,0.08)]"
            >
              <div className="max-h-[400px] overflow-y-auto p-5 space-y-4">
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className="flex gap-3"
                  >
                    <div
                      className="w-1 shrink-0 self-stretch"
                      style={{ backgroundColor: msg.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="font-mono text-xs font-medium tracking-wider uppercase"
                          style={{ color: msg.color }}
                        >
                          {msg.voice}
                        </span>
                        {msg.typing && (
                          <span className="flex gap-0.5">
                            {[0, 1, 2].map(d => (
                              <span
                                key={d}
                                className="w-1 h-1 bg-[rgba(245,241,232,0.48)] rounded-full animate-convection"
                                style={{ animationDelay: `${d * 0.2}s` }}
                              />
                            ))}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[rgba(245,241,232,0.85)] leading-relaxed">
                        {msg.text}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Value-Lock Actions */}
              {!isDebating && messages.length > 0 && (
                <div className="flex items-center gap-3 px-5 py-3 border-t border-[rgba(245,241,232,0.08)] bg-[#0A0A0A]/50">
                  <button
                    onClick={() => handleValueLockAction('save')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono text-[rgba(245,241,232,0.72)] border border-[rgba(245,241,232,0.08)] hover:border-[#FF4D00] hover:text-[#FF4D00] transition-all"
                  >
                    {!user && <Lock className="w-3 h-3" />}
                    <Download className="w-3 h-3" />
                    Save
                  </button>
                  <button
                    onClick={() => handleValueLockAction('share')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono text-[rgba(245,241,232,0.72)] border border-[rgba(245,241,232,0.08)] hover:border-[#00D4FF] hover:text-[#00D4FF] transition-all"
                  >
                    {!user && <Lock className="w-3 h-3" />}
                    <Share2 className="w-3 h-3" />
                    Share
                  </button>
                  <span className="ml-auto text-[10px] font-mono text-[rgba(245,241,232,0.32)]">
                    {user ? '5 VOICES · 3 ROUNDS' : 'SIGN UP TO UNLOCK PREMIUM MODELS'}
                  </span>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Auth Gate Modal */}
      <AnimatePresence>
        {showAuthGate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(10,10,10,0.88)' }}
            onClick={() => setShowAuthGate(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] p-8 max-w-sm w-full mx-4"
            >
              <MessageSquare className="w-8 h-8 text-[#FF4D00] mb-4" />
              <h3 className="text-xl font-display text-[#F5F1E8] mb-2">
                SAVE YOUR DEBATE
              </h3>
              <p className="text-sm text-[rgba(245,241,232,0.72)] mb-6 font-body">
                Create a free account to save, export, and share your debates. Unlock premium models and longer sessions.
              </p>
              <a
                href="/signup"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-all"
              >
                Start Debating Free
                <ChevronRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => setShowAuthGate(false)}
                className="w-full mt-3 text-xs text-[rgba(245,241,232,0.48)] hover:text-[rgba(245,241,232,0.72)] transition-colors"
              >
                Continue without saving
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function getSimulatedResponse(voice, prompt) {
  const responses = {
    'The Strategist': `Looking at this from a strategic lens, the key consideration is long-term positioning. The decision framework here should weigh market timing against organizational readiness. I'd recommend a phased approach with clear milestones.`,
    'Devil\'s Advocate': `I need to push back on the assumptions here. We're treating this as a binary choice when there may be a third path. What if the premise itself is flawed? Consider the opportunity cost of committing in either direction.`,
    'The Pragmatist': `Let's ground this in reality. What resources do we actually have? What's the timeline? The theoretically optimal choice means nothing if we can't execute. I'd prioritize the path with lowest execution risk.`,
    'Risk Analyst': `The risk profile here is asymmetric. One path has bounded downside with unbounded upside; the other reverses that ratio. We should also consider second-order effects that aren't immediately obvious.`,
    'The Synthesizer': `Integrating the perspectives shared: the strategic case is clear, but the execution concerns are valid. The dissenting view reveals an assumption worth testing. I propose we validate that assumption before committing.`,
  };
  return responses[voice] || 'Analyzing the decision...';
}
